import { Component, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { UserDetailComponent } from '../user-detail/user-detail.component';
import { SecAdminUserService } from '../../services/sec-admin-user.service';
import { ISecAdminUser } from '../../interfaces/sec-admin-user';
import { ApplicationService } from '../../services/application.service';
import { IApplication } from '../../interfaces/application';
import { SiteAdminService } from '../../services/site-admin.service';
import { ISiteAdmin } from '../../interfaces/site-admin';
import { UserService } from '../../services/user.service';
import { UserAction } from '../../enums/user-action.enum';
import { IUserDetail } from '../../interfaces/user-detail';
import { IUserDetailResult } from '../../interfaces/user-detail-result';
import { IAddAdmin } from '../../interfaces/add-admin';
import { IUpdateAdmin } from '../../interfaces/update-admin';
import { ConfirmationComponent } from '../shared/confirmation/confirmation.component';
import { IUser } from '../../interfaces/user';

@Component({
    selector: 'app-site-security',
    templateUrl: './site-security.component.html',
    styles: [],
    standalone: false
})
export class SiteSecurityComponent implements OnInit {
  @ViewChild("adminDetail") adminDetail: UserDetailComponent = {} as UserDetailComponent;
  @ViewChild("confirmDelete") confirmDelete: ConfirmationComponent = {} as ConfirmationComponent;

  public currentUser: ISecAdminUser;
  public applications: IApplication[];
  public siteAdmins: ISiteAdmin[];
  public siteAdminForm: UntypedFormGroup;
  public errors: string[] = new Array<string>();

  public siteAdminHelpMessage: string = "";
  public accountAllowLoginHelpMessage: string;
  private selectedApplication: IApplication;
  private applicationId: number;

  public get applicationTextbox() {
    return this.siteAdminForm.get("applicationTextbox");
  }

  public get application() {
    return this.siteAdminForm.get("application");
  }

  constructor(
    private readonly titleService: Title,
    private readonly fb: UntypedFormBuilder,
    private readonly secAdminUserService: SecAdminUserService,
    private readonly applicationService: ApplicationService,
    private readonly siteAdminService: SiteAdminService,
    private readonly userService: UserService
  ) { }

  ngOnInit(): void {
    this.titleService.setTitle("Site Security Management");

    this.siteAdminForm = this.fb.group({
      applicationTextbox: [""],
      application: [""]
    });

    this.currentUser = this.secAdminUserService.getCurrentUser();

    this.applicationService.getSiteAdminApplications(this.currentUser.username)
      .subscribe(data => {
        this.applications = data;
        if (this.applications.length == 1)
          this.applicationTextbox.setValue(this.applications[0].name);
        else if (this.applications.length > 1)
          this.application.setValue(this.applications[0].id);
        this.LoadSiteAdmins(this.applications[0].id);

        this.selectedApplication = data[0];
        this.siteAdminHelpMessage = this.selectedApplication.siteSecurityHelpMessage;
        this.accountAllowLoginHelpMessage = this.selectedApplication.accountAllowLoginHelpMessage;
      }, error => {
        this.errors.push(error.message);
      });
  }

  public onApplicationChange(): void {
    let application: IApplication = this.applications.find(x => x.id === this.application.value);
    if (application == null)
      this.siteAdmins = null;

    this.LoadSiteAdmins(application.id);
    this.selectedApplication = application;
    this.siteAdminHelpMessage = this.selectedApplication.siteSecurityHelpMessage;
  }

  private LoadSiteAdmins(appId: number): void {
    this.applicationId = appId;
    this.siteAdminService.getSiteAdmins(appId)
      .subscribe(data => {
        this.siteAdmins = data;
      }, error => {
        this.errors.push(error.message);
      })
  }
  
  public NewSiteAdmin() {
    this.adminDetail.open(this.applicationId, null, false);
  }

  public SiteAdminDetails(username: string) {
    this.userService.getUser(this.applicationId, username)
      .subscribe(data => {
        var userDetail: IUserDetail = {
          id: data.id,
          username: data.username,
          firstName: data.firstName,
          lastName: data.lastName,
          displayName: data.displayName,
          allowLogin: data.allowLogin,
          allowAppLogin: false,
          roleIds: []
        }
        this.adminDetail.open(this.applicationId, userDetail, false);
      }, error => {
        this.errors.push(error.message);
      })
  }

  public RemoveSiteAdmin(id: string, username: string) {
    this.confirmDelete.prompt = "Are you sure you want to remove the User '" + username + "' from the list of Site Security Administrators?";
    this.confirmDelete.open(id);
  }

  public renderAllowLogin(allowLogin: boolean) {
    if (allowLogin)
      return "Yes";
    else
      return "No";
  }

  public onDeleteConfim(id: string) {
    this.siteAdminService.removeSiteAdmin(this.applicationId, id)
      .subscribe(() => {
        this.LoadSiteAdmins(this.applicationId);
      }, error => {
        this.errors.push(error.message);
      })
  }
  
  public ResetSiteAdminPassword(id: string) {
    this.userService.resetPassword(this.applicationId, 3 /* Sec Admin */, id)
      .subscribe(() => {

      }, error => {
        this.errors.push(error.message);
      })
  }

  public isAdminDuplicate = (userId: string) => {
    return (this.siteAdmins.findIndex(x => x.id == userId) >= 0);
  }

  public isAdminAlreadyAssigned = (user: IUser) => {
    return false;
  }

  public onAdminUpdate(result: IUserDetailResult): void {
    switch (result.action) {
      case UserAction.AddNew: {
        let addSiteAdmin: IAddAdmin = {
          username: result.user.username,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          displayName: result.user.displayName,
          allowLogin: result.user.allowLogin,
        };
        this.siteAdminService.addSiteAdmin(this.applicationId, 3 /* Sec Admin */, addSiteAdmin)
          .subscribe(() => {
            this.LoadSiteAdmins(this.applicationId);
          }, error => {
            this.errors.push(error.message);
          })
        break;
      }
      case UserAction.AddFromExisting: {
        this.siteAdminService.addSiteAdminFromExisting(this.applicationId, result.user.id, result.user.allowLogin)
          .subscribe(() => {
            this.LoadSiteAdmins(this.applicationId);
          }, error => {
            this.errors.push(error.message);
          })
        break;
      }
      case UserAction.Update: {
        let updateSiteAdmin: IUpdateAdmin = {
          username: result.user.username,
          lastName: result.user.lastName,
          firstName: result.user.firstName,
          displayName: result.user.displayName,
          allowLogin: result.user.allowLogin
        };
        this.siteAdminService.updateSiteAdmin(result.user.id, this.applicationId, updateSiteAdmin)
          .subscribe(() => {
            this.LoadSiteAdmins(this.applicationId);
          }, error => {
            this.errors.push(error.message);
          })
        break;
      }
    }
  }

  public closeError(error: string): void {
    this.errors.splice(this.errors.indexOf(error), 1);
  }
}
